import React, { useState, useEffect } from 'react';
import { Card, Form, Input, Button, Upload, message, Space } from 'antd';
import { UploadOutlined, SaveOutlined } from '@ant-design/icons';
import settingsService from '../../services/settingsService';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from 'react-i18next';

const Settings = () => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [signatureUrl, setSignatureUrl] = useState('');
    const { user } = useAuth();
    const { t } = useTranslation();

    useEffect(() => {
        fetchSettings();
    }, []);

    const fetchSettings = async () => {
        try {
            const data = await settingsService.getSettings(user._id);
            form.setFieldsValue({
                doctorName: data.doctorName,
                clinicAddress: data.clinicAddress,
                clinicPhone: data.clinicPhone
            });
            setSignatureUrl(data.doctorSignature);
        } catch (error) {
            message.error('Failed to fetch settings');
        }
    };

    const handleSubmit = async (values) => {
        setLoading(true);
        try {
            await settingsService.updateSettings(user._id, values);
            message.success('Settings updated successfully');
        } catch (error) {
            message.error('Failed to update settings');
        } finally {
            setLoading(false);
        }
    };

    const handleSignatureUpload = async (file) => {
        try {
            const response = await settingsService.uploadSignature(user._id, file);
            setSignatureUrl(response.fileUrl);
            message.success('Signature uploaded successfully');
        } catch (error) {
            message.error('Failed to upload signature');
        }
        return false; // Prevent default upload behavior
    };

    return (
        <div style={{ padding: '24px' }}>
            <Card
                title={
                    <h2 style={{
                        margin: 0,
                        background: 'linear-gradient(135deg, #028090 0%, #00A896 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        fontSize: '24px',
                        fontWeight: 700
                    }}>
                        {t('Clinic Settings')}
                    </h2>
                }
                style={{
                    maxWidth: '800px',
                    margin: '0 auto',
                    borderRadius: '12px',
                    boxShadow: '0 4px 12px rgba(2, 128, 144, 0.08)'
                }}
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmit}
                >
                    <Form.Item
                        label="Doctor Name"
                        name="doctorName"
                        rules={[{ required: true, message: 'Please enter doctor name' }]}
                    >
                        <Input size="large" placeholder="Enter doctor name" />
                    </Form.Item>

                    <Form.Item
                        label="Clinic Address"
                        name="clinicAddress"
                        rules={[{ required: true, message: 'Please enter clinic address' }]}
                    >
                        <Input.TextArea
                            size="large"
                            rows={3}
                            placeholder="Enter clinic address"
                        />
                    </Form.Item>

                    <Form.Item
                        label="Clinic Phone"
                        name="clinicPhone"
                        rules={[{ required: true, message: 'Please enter clinic phone' }]}
                    >
                        <Input size="large" placeholder="Enter clinic phone number" />
                    </Form.Item>

                    <Form.Item
                        label="Doctor Signature"
                    >
                        <Space direction="vertical" style={{ width: '100%' }}>
                            <Upload
                                beforeUpload={handleSignatureUpload}
                                maxCount={1}
                                accept="image/*"
                            >
                                <Button icon={<UploadOutlined />}>Upload Signature</Button>
                            </Upload>
                            {signatureUrl && (
                                <div style={{
                                    marginTop: '16px',
                                    padding: '16px',
                                    background: '#f5f7fa',
                                    borderRadius: '8px',
                                    textAlign: 'center'
                                }}>
                                    <img
                                        src={signatureUrl}
                                        alt="Signature"
                                        style={{
                                            maxWidth: '200px',
                                            maxHeight: '100px',
                                            objectFit: 'contain'
                                        }}
                                    />
                                </div>
                            )}
                        </Space>
                    </Form.Item>

                    <Form.Item>
                        <Button
                            type="primary"
                            htmlType="submit"
                            loading={loading}
                            icon={<SaveOutlined />}
                            size="large"
                            style={{
                                background: 'linear-gradient(135deg, #028090 0%, #00A896 100%)',
                                border: 'none',
                                fontWeight: 600
                            }}
                        >
                            Save Settings
                        </Button>
                    </Form.Item>
                </Form>
            </Card>
        </div>
    );
};

export default Settings;
